<script>
  import { get } from 'svelte/store'
  import { getPaper, BETS_PAGE } from '../../api.js'
  import {
    paperData,
    bets,
    betsHasMore,
    betsLoading,
    horizonFilter,
    setHorizonFilter,
    assetFilter,
    setAssetFilter,
    paperDay,
    paperCalendar,
  } from '../../stores.js'
  import { refreshCore } from '../../lib/polling.js'
  import { formatPnlPct, mskToday } from '../../lib/tradingDay.js'
  import BetFeed from '../../components/BetFeed.svelte'
  import HorizonTabs from '../../components/HorizonTabs.svelte'
  import DayPicker from '../../components/DayPicker.svelte'

  let skipsOpen = false
  let error = ''
  let switching = false
  let localHz = 'all'
  let localAsset = 'all'

  const FALLBACK_ASSET_TABS = [
    { id: 'all', label: 'Все' },
    { id: 'btc', label: 'BTC' },
    { id: 'eth', label: 'ETH' },
    { id: 'eth-exit', label: 'ETH 15m dogon' },
  ]

  $: feed = ($paperData && $paperData.feed) || {}
  $: skips = feed.skips_collapsed || null
  $: emptyRu =
    ($paperData && $paperData.health && $paperData.health.empty_feed_ru) ||
    ($paperData && $paperData.ui_copy && $paperData.ui_copy.empty_feed) ||
    'Ставок пока нет.'
  $: assetTabs = (() => {
    const raw = $paperData && $paperData.bets_roster
    if (!Array.isArray(raw) || !raw.length) return FALLBACK_ASSET_TABS
    const tabs = raw
      .map((row) => ({
        id: String(row?.id || '')
          .trim()
          .toLowerCase(),
        label: String(row?.label || '').trim(),
      }))
      .filter((t) => t.id && t.label)
    return tabs.length ? tabs : FALLBACK_ASSET_TABS
  })()
  $: if ($horizonFilter && $horizonFilter !== localHz) localHz = $horizonFilter
  $: if ($assetFilter && $assetFilter !== localAsset) localAsset = $assetFilter
  $: showHz = localHz === 'all'
  $: showAsset = localAsset === 'all'

  $: cal = $paperCalendar || ($paperData && $paperData.calendar) || null
  $: calDays = (cal && cal.days) || {}
  $: todayId = (cal && cal.today) || ($paperData && $paperData.today) || mskToday()
  $: selectedDay = $paperDay || ($paperData && $paperData.day) || todayId
  $: daySummary = ($paperData && $paperData.day_summary) || null
  $: dayPctLabel =
    daySummary && daySummary.pnl_pct != null ? formatPnlPct(daySummary.pnl_pct) : ''

  async function onRefresh() {
    skipsOpen = false
    error = ''
    await refreshCore({ resetBets: true })
  }

  async function onHzChange(hz) {
    if (hz === get(horizonFilter)) return
    localHz = hz
    setHorizonFilter(hz)
    switching = true
    error = ''
    skipsOpen = false
    try {
      await refreshCore({ resetBets: true })
    } finally {
      switching = false
    }
  }

  async function onAssetChange(asset) {
    if (asset === get(assetFilter)) return
    localAsset = asset
    setAssetFilter(asset)
    switching = true
    error = ''
    skipsOpen = false
    try {
      await refreshCore({ resetBets: true })
    } finally {
      switching = false
    }
  }

  async function onDayChange(e) {
    const day = e.detail && e.detail.day
    if (!day || day === get(paperDay)) return
    paperDay.set(day)
    switching = true
    error = ''
    skipsOpen = false
    try {
      await refreshCore({ resetBets: true })
    } finally {
      switching = false
    }
  }

  async function onMonthChange(e) {
    const month = e.detail && e.detail.month
    if (!month) return
    try {
      const { res, data } = await getPaper({
        betsOffset: 0,
        betsLimit: BETS_PAGE,
        horizon: get(horizonFilter),
        asset: get(assetFilter),
        day: get(paperDay) || selectedDay,
        month,
      })
      if (res?.ok && data?.calendar) paperCalendar.set(data.calendar)
    } catch {
      /* ignore month prefetch errors */
    }
  }

  async function loadMore() {
    if (get(betsLoading) || !get(betsHasMore)) return
    betsLoading.set(true)
    error = ''
    try {
      const offset = get(bets).length
      const { res, data } = await getPaper({
        betsOffset: offset,
        betsLimit: BETS_PAGE,
        horizon: get(horizonFilter),
        asset: get(assetFilter),
        day: get(paperDay) || selectedDay,
      })
      if (!res.ok || !data || !data.ok) {
        error = 'Не удалось догрузить ставки'
        return
      }
      const page = (data.feed && data.feed.bets) || []
      bets.update((cur) => cur.concat(page))
      betsHasMore.set(!!(data.feed && data.feed.has_more))
      if (data.feed && data.feed.skips_collapsed) {
        paperData.update((p) => {
          if (!p) return data
          return {
            ...p,
            feed: {
              ...p.feed,
              skips_collapsed: data.feed.skips_collapsed,
              has_more: data.feed.has_more,
            },
          }
        })
      }
    } catch {
      error = 'Ошибка сети'
    } finally {
      betsLoading.set(false)
    }
  }
</script>

<div class="pm-section-head">
  <h2>Активность</h2>
  <div class="trades-tools">
    <DayPicker
      value={selectedDay}
      today={todayId}
      days={calDays}
      month={cal && cal.month}
      disabled={switching}
      on:change={onDayChange}
      on:month={onMonthChange}
    />
    <button type="button" class="pm-btn" on:click={onRefresh} disabled={switching}>Обновить</button>
  </div>
</div>

<div class="trades-hz">
  <HorizonTabs value={localHz} on:change={(e) => onHzChange(e.detail)} />
  <div class="trades-assets" role="tablist" aria-label="Монета">
    {#each assetTabs as tab}
      <button
        type="button"
        role="tab"
        class="trades-asset"
        class:is-on={localAsset === tab.id}
        aria-selected={localAsset === tab.id}
        on:click={() => onAssetChange(tab.id)}
        >{tab.label}</button
      >
    {/each}
  </div>
  {#if dayPctLabel}
    <span
      class="trades-day-pct mono"
      class:is-up={daySummary && daySummary.pnl_pct > 0}
      class:is-down={daySummary && daySummary.pnl_pct < 0}>{dayPctLabel}</span
    >
  {/if}
</div>

{#if error}
  <div class="pm-error">{error}</div>
{/if}

<section class="pm-surface" aria-label="Лента ставок">
  {#if skips && skips.count}
    <button
      type="button"
      class="pm-skips-toggle"
      class:is-open={skipsOpen}
      on:click={() => (skipsOpen = !skipsOpen)}
    >
      <span class="pm-skips-chevron" aria-hidden="true">▶</span>
      <span>{skips.count} пропусков скрыто (не в ленте ставок)</span>
    </button>
    {#if skipsOpen}
      <div class="pm-skips-panel">
        {#each skips.rows || [] as row}
          <div>
            {#if row.horizon}<span class="pm-hz-pill">{row.horizon}</span>{/if}
            {row.timestamp || ''} · {row.slug || row.market || '—'} · {row.skip_reason || 'SKIP'}
          </div>
        {/each}
      </div>
    {/if}
  {/if}

  {#if switching}
    <p class="pm-empty">Переключение…</p>
  {:else if !$bets.length}
    <p class="pm-empty">{emptyRu}</p>
  {:else}
    <BetFeed items={$bets} showHorizon={showHz} showAsset={showAsset} />
  {/if}

  {#if $betsHasMore}
    <button type="button" class="pm-btn" style="margin-top:0.85rem" disabled={$betsLoading} on:click={loadMore}>
      {$betsLoading ? 'Загрузка…' : `Ещё ${BETS_PAGE}`}
    </button>
  {/if}
</section>

<style>
  .trades-tools {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
  }
  .trades-hz {
    margin: 0 0 0.85rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.65rem 1rem;
  }
  .trades-assets {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }
  .trades-asset {
    font: inherit;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 0.28rem 0.55rem;
    border-radius: 999px;
    border: 1px solid var(--pm-line);
    background: transparent;
    color: var(--pm-ink-mute);
    cursor: pointer;
  }
  .trades-asset.is-on {
    color: var(--pm-ink);
    border-color: rgba(111, 191, 138, 0.45);
    background: rgba(111, 191, 138, 0.12);
  }
  .trades-day-pct {
    font-size: 0.85rem;
    font-weight: 650;
    color: var(--pm-ink-soft);
  }
  .trades-day-pct.is-up {
    color: var(--pm-ok);
  }
  .trades-day-pct.is-down {
    color: var(--pm-bad);
  }
  .pm-hz-pill {
    display: inline-block;
    margin-right: 0.35rem;
    padding: 0.05rem 0.4rem;
    border-radius: 999px;
    border: 1px solid var(--pm-line);
    font-family: var(--pm-mono);
    font-size: 0.62rem;
    font-weight: 500;
    color: var(--pm-ink-soft);
    background: var(--pm-paper-alt);
  }
</style>
